import { c as _c } from "react/compiler-runtime";
import { IcoInfo24 } from '@onefootprint/icons';
import { type DocumentRequirement, DocumentUploadSettings } from '@onefootprint/types';
import { useEffect, useLayoutEffect, useState } from 'react';
import { getLogger } from '@/idv/utils';
import type { NavigationHeaderLeftButtonProps } from '../../../../components';
import { HeaderTitle, NavigationHeader, useLayoutOptions } from '../../../../components';
import useProcessImage, { isProcessingResultError } from '../../hooks/use-process-image';
import type { CameraErrorCause, CaptureKind, ReceivedImagePayload } from '../../types';
import { isDesktop, isDocument, isFace, isMobile } from '../../utils/capture';
import Camera from '../camera';
import AutoCaptureDoc from '../camera/auto-capture-doc';
import AutoCaptureFace from '../camera/auto-capture-face';
import type { AutoCaptureKind, CaptureStatus, DeviceKind } from '../camera/types';
import type { CameraSide } from '../camera/utils/get-camera-options';
import Loading from '../loading';
import Instructions from './components/instructions';
import Preview from './components/preview';
import useCaptureTelemetry from './hooks/use-capture-telemetry';
type HeaderTextType = {
  camera: string;
  preview: string;
};
type PhotoCaptureProps = {
  autocaptureKind: AutoCaptureKind;
  cameraKind: CameraSide;
  deviceKind: DeviceKind;
  allowPdf: boolean;
  sideName?: string;
  docName?: string;
  /** Untranslated document side, for telemetry. sideName is localized and unsafe to record. */
  side?: string;
  requirement: DocumentRequirement;
  hasBadConnectivity?: boolean;
  onCameraStuck?: () => void;
  onCameraErrored?: (cause: CameraErrorCause) => void;
  onCameraAccessDenied?: () => void;
  onBack?: () => void;
  onComplete: (payload: ReceivedImagePayload) => void;
  outlineHeightRatio: number; // with respect to the video width (not height)
  outlineWidthRatio: number; // with respect to the video width
  subtitle?: Partial<HeaderTextType>;
  title: HeaderTextType;
};
const DesktopNavProps: NavigationHeaderLeftButtonProps = {
  variant: 'close',
  confirmClose: true
};
const {
  logWarn,
  logInfo
} = getLogger({
  location: 'photo-capture'
});
const PhotoCapture = t0 => {
  const $ = _c(64);
  const {
    autocaptureKind,
    cameraKind,
    deviceKind,
    allowPdf,
    sideName,
    docName,
    side,
    requirement,
    hasBadConnectivity,
    onCameraStuck,
    onCameraErrored,
    onCameraAccessDenied,
    onBack,
    onComplete,
    outlineHeightRatio,
    outlineWidthRatio,
    subtitle,
    title
  } = t0;
  const allowUpload = requirement.uploadSettings !== DocumentUploadSettings.captureOnlyOnMobile && requirement.uploadSettings !== DocumentUploadSettings.requireMobileCapture;
  let t1;
  if ($[0] !== allowPdf) {
    t1 = {
      allowPdf
    };
    $[0] = allowPdf;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    processImageUrl
  } = useProcessImage(t1);
  const [image, setImage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [captureKind, setCaptureKind] = useState();
  const [showInstructions, setShowInstructions] = useState(false);
  const [isCaptured, setIsCaptured] = useState(false);
  const [autocaptureFeedback, setAutocaptureFeedback] = useState("detecting");
  let t2;
  if ($[2] !== autocaptureFeedback || $[3] !== autocaptureKind || $[4] !== side) {
    t2 = {
      autocaptureKind,
      side,
      status: autocaptureFeedback
    };
    $[2] = autocaptureFeedback;
    $[3] = autocaptureKind;
    $[4] = side;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const {
    reportCapture,
    reportCameraStuck
  } = useCaptureTelemetry(t2);
  const {
    footer: t3,
    body: t4
  } = useLayoutOptions();
  const {
    set: updateFooter
  } = t3;
  const {
    set: updateBody
  } = t4;
  let t5;
  let t6;
  if ($[6] !== deviceKind || $[7] !== image || $[8] !== updateFooter) {
    t5 = () => {
      if (isMobile(deviceKind)) {
        return _temp;
      }
      if (image) {
        updateFooter({
          visible: true
        });
      } else {
        updateFooter({
          visible: false
        });
      }
      return () => {
        updateFooter({
          visible: true
        });
      };
    };
    t6 = [image, deviceKind, updateFooter];
    $[6] = deviceKind;
    $[7] = image;
    $[8] = updateFooter;
    $[9] = t5;
    $[10] = t6;
  } else {
    t5 = $[9];
    t6 = $[10];
  }
  useLayoutEffect(t5, t6);
  let t7;
  let t8;
  if ($[11] !== deviceKind || $[12] !== image || $[13] !== updateBody) {
    t7 = () => {
      if (!isMobile(deviceKind)) {
        return _temp2;
      }
      updateBody({
        fullBleed: !image
      });
      return () => {
        updateBody({
          fullBleed: false
        });
      };
    };
    t8 = [image, deviceKind, updateBody];
    $[11] = deviceKind;
    $[12] = image;
    $[13] = updateBody;
    $[14] = t7;
    $[15] = t8;
  } else {
    t7 = $[14];
    t8 = $[15];
  }
  useLayoutEffect(t7, t8);
  let t9;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      logInfo("Retaking photo");
      setImage(null);
      setIsCaptured(false);
    };
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const handleRetake = t9;
  let t10;
  if ($[17] !== captureKind || $[18] !== hasBadConnectivity || $[19] !== image || $[20] !== onComplete || $[21] !== processImageUrl) {
    t10 = async () => {
      if (!image) {
        logWarn("Captured image could not be confirmed and submitted - retaking the image");
        return;
      }
      if (!captureKind) {
        logWarn("Captured kind could not be determined - retaking the image");
        return;
      }
      setIsLoading(true);
      const processResult = await processImageUrl(image, hasBadConnectivity);
      if (isProcessingResultError(processResult)) {
        setIsLoading(false);
        handleRetake();
        logWarn("Captured image could not be processed - retaking the image");
        return;
      }
      setIsLoading(false);
      const {
        file,
        extraCompressed
      } = processResult;
      onComplete({
        captureKind,
        extraCompressed,
        imageFile: file,
        forcedUpload: false
      });
    };
    $[17] = captureKind;
    $[18] = hasBadConnectivity;
    $[19] = image;
    $[20] = onComplete;
    $[21] = processImageUrl;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  const handleConfirm = t10;
  let t11;
  if ($[23] !== onCameraErrored) {
    t11 = cause => {
      logInfo("Camera error occurred", {
        cause
      });
      onCameraErrored?.(cause);
    };
    $[23] = onCameraErrored;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  const handleError = t11;
  let t12;
  if ($[25] !== deviceKind || $[26] !== handleConfirm || $[27] !== image) {
    t12 = () => {
      if (image && isMobile(deviceKind)) {
        handleConfirm();
      }
    };
    $[25] = deviceKind;
    $[26] = handleConfirm;
    $[27] = image;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== image) {
    t13 = [image];
    $[29] = image;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  useEffect(t12, t13);
  let t14;
  if ($[31] !== reportCapture) {
    t14 = (newImage, newCaptureKind) => {
      reportCapture(newCaptureKind);
      setImage(newImage);
      setCaptureKind(newCaptureKind);
    };
    $[31] = reportCapture;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  const handleCapture = t14;
  let t15;
  if ($[33] !== onComplete || $[34] !== reportCapture) {
    t15 = payload => {
      reportCapture(payload.captureKind);
      onComplete(payload);
    };
    $[33] = onComplete;
    $[34] = reportCapture;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  const handleUpload = t15;
  let t16;
  if ($[36] !== onCameraStuck || $[37] !== reportCameraStuck) {
    t16 = () => {
      reportCameraStuck();
      onCameraStuck?.();
    };
    $[36] = onCameraStuck;
    $[37] = reportCameraStuck;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  const handleCameraStuck = t16;
  let t17;
  if ($[39] !== allowPdf || $[40] !== allowUpload || $[41] !== autocaptureFeedback || $[42] !== autocaptureKind || $[43] !== cameraKind || $[44] !== deviceKind || $[45] !== docName || $[46] !== handleCameraStuck || $[47] !== handleCapture || $[48] !== handleConfirm || $[49] !== handleError || $[50] !== handleUpload || $[51] !== hasBadConnectivity || $[52] !== image || $[53] !== isCaptured || $[54] !== isLoading || $[55] !== onBack || $[56] !== onCameraAccessDenied || $[57] !== outlineHeightRatio || $[58] !== outlineWidthRatio || $[59] !== showInstructions || $[60] !== sideName || $[61] !== subtitle?.camera || $[62] !== title) {
    t17 = image ? <>{isDesktop(deviceKind) && <><div className="mb-6"><NavigationHeader leftButton={DesktopNavProps} /><HeaderTitle title={title.preview} /></div><Preview imageSrc={image} onRetake={handleRetake} onConfirm={handleConfirm} isLoading={isLoading} cameraKind={cameraKind} deviceKind={deviceKind} /></>}{isMobile(deviceKind) && <div className="flex flex-col items-center justify-center h-full"><Loading step="process" /></div>}</> : <>{isDesktop(deviceKind) && <div className="mb-6"><NavigationHeader leftButton={DesktopNavProps} /><HeaderTitle title={title.camera} subtitle={subtitle?.camera} /></div>}{isMobile(deviceKind) && <NavigationHeader leftButton={{
        variant: "back",
        onBack
      }} rightButton={{
        icon: IcoInfo24,
        onClick: () => setShowInstructions(true)
      }} content={{
        kind: "static",
        title: title.camera
      }} />}<Camera autoCaptureFeedback={autocaptureFeedback} autoCaptureKind={autocaptureKind} cameraSide={cameraKind} deviceKind={deviceKind} docName={docName} sideName={sideName} onCapture={handleCapture} onUpload={handleUpload} hasBadConnectivity={hasBadConnectivity} onCameraError={isMobile(deviceKind) ? handleError : undefined} onCameraAccessDenied={onCameraAccessDenied} outlineHeightRatio={outlineHeightRatio} outlineWidthRatio={outlineWidthRatio} setIsCaptured={setIsCaptured} allowPdf={allowPdf} onCameraStuck={handleCameraStuck} allowUpload={allowUpload}>{t18 => {
          const {
            canvasAutoCaptureRef,
            feedbackPositionFromBottom,
            videoResolution,
            onDetectionComplete,
            onDetectionReset,
            outlineHeight,
            outlineWidth,
            videoRef,
            videoSize
          } = t18;
          if (isDocument(autocaptureKind)) {
            return <AutoCaptureDoc canvasAutoCaptureRef={canvasAutoCaptureRef} feedbackPositionFromBottom={feedbackPositionFromBottom} isCaptured={isCaptured} videoResolution={videoResolution} onDetectionComplete={onDetectionComplete} onDetectionReset={onDetectionReset} outlineHeight={outlineHeight} outlineWidth={outlineWidth} setAutoCaptureFeedback={setAutocaptureFeedback} videoRef={videoRef} videoSize={videoSize} />;
          }
          return isFace(autocaptureKind) ? <AutoCaptureFace canvasAutoCaptureRef={canvasAutoCaptureRef} feedbackPositionFromBottom={feedbackPositionFromBottom} isCaptured={isCaptured} onDetectionComplete={onDetectionComplete} onDetectionReset={onDetectionReset} outlineWidth={outlineWidth} setAutoCaptureFeedback={setAutocaptureFeedback} videoRef={videoRef} videoSize={videoSize} videoResolution={videoResolution} /> : null;
        }}</Camera><Instructions onClose={() => setShowInstructions(false)} isOpen={showInstructions} autoCaptureKind={autocaptureKind} /></>;
    $[39] = allowPdf;
    $[40] = allowUpload;
    $[41] = autocaptureFeedback;
    $[42] = autocaptureKind;
    $[43] = cameraKind;
    $[44] = deviceKind;
    $[45] = docName;
    $[46] = handleCameraStuck;
    $[47] = handleCapture;
    $[48] = handleConfirm;
    $[49] = handleError;
    $[50] = handleUpload;
    $[51] = hasBadConnectivity;
    $[52] = image;
    $[53] = isCaptured;
    $[54] = isLoading;
    $[55] = onBack;
    $[56] = onCameraAccessDenied;
    $[57] = outlineHeightRatio;
    $[58] = outlineWidthRatio;
    $[59] = showInstructions;
    $[60] = sideName;
    $[61] = subtitle?.camera;
    $[62] = title;
    $[63] = t17;
  } else {
    t17 = $[63];
  }
  return t17;
};
export default PhotoCapture;
function _temp() {}
function _temp2() {}